import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { Sparkle, Upload } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import useIsMobile from 'hooks/useIsMobile';
import { useImageEditorStore } from 'components/ImageEditor/ImageEditorStoreProvider';
import { AllStoreType } from 'components/ImageEditor/store/imageEditor.type';

const ObjectReplacerHero = () => {
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

  return (
    <div className="space-y-4 md:space-y-8">
      <span className="flex items-center gap-2">
        <img
          src={
            isDarkMode ? '/object-replacer-dark.png' : '/object-replacer.png'
          }
          className="max-h-[120px] md:max-h-[146px] h-full"
          alt="Object Replacer"
        />
      </span>
      <div className="md:space-y-5 flex flex-col gap-2">
        <h1 className="text-2xl md:text-5xl font-medium text-text-primary lg:leading-[60px]">
          Welcome to <br className="hidden md:block" /> Object Replacer
        </h1>
        <p className="text-sm lg:text-xl font-normal text-text-primary md:w-[360px] mb-6 lg:mb-0">
          Seamlessly swap and replace elements in your images with our
          AI-powered tool. Effortlessly edit, modify, or enhance your visuals by
          changing objects, backgrounds, or details with precision. Transform
          your images in just a few clicks!
        </p>
      </div>
    </div>
  );
};

const UploadImageForm = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setImageUrl, imageUrl } = useImageEditorStore<AllStoreType>(
    (state) => state
  );

  useEffect(() => {
    if (imageUrl) {
      navigate('/object-replacer/result');
    }
  }, [imageUrl, navigate]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleFileSelect = (file: File) => {
    if (file) {
      setImageUrl(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex lg:w-[600px] max-w-[600px] ">
        <div className="text-text-primary mr-[50px] px-6 lg:px-0">
          <div className="text-[15px] ">STEP 1</div>
          <div className="text-[25px] font-bold">Upload your Image</div>
        </div>
      </div>
      <div className="bg-background rounded-lg p-4 sm:p-10 space-y-4 h-fit max-w-[640px] w-full">
        <div
          className="border-2 border-dashed h-[200px] sm:h-[267px] border-primary rounded-lg p-4 sm:p-12 text-center cursor-pointer relative"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => document.getElementById('fileInput')?.click()}
        >
          <input
            type="file"
            id="fileInput"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleFileSelect(e.target.files[0]);
              }
            }}
            accept="image/png, image/jpeg, image/jpg"
          />
          {imageUrl && (
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={
                  typeof imageUrl === 'string'
                    ? imageUrl
                    : URL.createObjectURL(imageUrl)
                }
                alt="Preview"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          )}
          {!imageUrl && (
            <div className="flex h-full flex-col items-center justify-center">
              <Upload className="mx-auto mb-4 text-primary" size={24} />
              <div className="text-base sm:text-lg mb-2 text-text-primary">
                {isMobile
                  ? 'Tap to choose image'
                  : 'Drag and drop or click to choose image'}
              </div>
              <div className="text-xs sm:text-sm text-text-secondary">
                PNG, JPG, JPEG (UP TO 5MB)
              </div>
            </div>
          )}
        </div>

        <div className="w-full lg:block">
          <Button
            disabled={!imageUrl}
            className="w-full gap-3 mt-2 text-sm disabled:cursor-not-allowed"
            variant={'customPrimary'}
          >
            <Sparkle />
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function GetStartedPage() {
  return (
    <div className="w-full bg-background-main rounded-lg rounded-b-none border border-background-main pb-16 mx-auto py-4 lg:py-14 flex flex-col justify-center items-center px-4 lg:px-11 bg-gradient-light dark:bg-gradient-dark bg-no-repeat bg-cover">
      <div className="flex flex-col md:flex-row md:justify-between max-w-[1260px] mx-auto w-full">
        <ObjectReplacerHero />
        <UploadImageForm />
      </div>
    </div>
  );
}
